import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Search,
  AccessTime,
  CheckCircle,
  Error,
  PlayArrow,
  PauseCircle,
  Timeline,
  TableChart,
  Assignment,
} from '@mui/icons-material';
import { useTenant } from '../hooks/useTenant';
import { ProcessInstanceDetailResponse } from '../types/process';
import { useProcessesService } from '../services/processesService';
import { CompactVariablesTable } from '../components/CompactVariablesTable';
import { ProcessIncidents } from '../components/ProcessIncidents';
import { UserTasks } from '../components/UserTasks';
import BpmnTimeline from '../components/BpmnTimeline';
import { createTimelineFromApiData } from '../utils/timelineConverter';
import { TimelineEvent, ProcessInstanceTimeline } from '../types/timeline';

const ProcessInstanceDetail: React.FC = () => {
  const { currentTenant, isTenantSelected } = useTenant();
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const processesService = useProcessesService();

  // Estados
  const [instanceDetail, setInstanceDetail] = useState<ProcessInstanceDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailedVariables, setDetailedVariables] = useState<Set<string>>(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState<TimelineEvent | null>(null);
  const [bpmnXml, setBpmnXml] = useState<string>('');
  const [bpmnLoading, setBpmnLoading] = useState(false);
  const [completeTimelineData, setCompleteTimelineData] = useState<ProcessInstanceTimeline | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Função para carregar detalhes da instância
  const loadInstanceDetail = useCallback(async () => {
    if (!currentTenant || !instanceId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const detail = await processesService.getProcessInstanceDetail(
        currentTenant.name,
        instanceId
      );
      setInstanceDetail(detail);
    } catch (err) {
      console.error('❌ Erro ao carregar detalhes da instância:', err);
      setError("Erro ao carregar detalhes da instância");
      setSnackbar({
        open: true,
        message: 'Erro ao carregar detalhes da instância',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [currentTenant, instanceId, processesService]);

  // Função para carregar BPMN
  const loadBpmn = useCallback(async (processDefinitionId: string) => {
    setBpmnLoading(true);
    try {
      const response = await processesService.getProcessXml(currentTenant!.name, processDefinitionId);
      setBpmnXml(response.xml);
    } catch (err) {
      console.error('❌ Erro ao carregar BPMN:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar diagrama BPMN',
        severity: 'warning',
      });
    } finally {
      setBpmnLoading(false);
    }
  }, [currentTenant, processesService]);

  // Carregar dados na montagem do componente
  useEffect(() => {
    if (isTenantSelected) {
      loadInstanceDetail();
    }
  }, [isTenantSelected, loadInstanceDetail]);

  // Carregar timeline quando os detalhes da instância estiverem disponíveis
  useEffect(() => {
    if (instanceDetail) {
      // Carregar BPMN
      loadBpmn(instanceDetail.instance.process_definition_id);
      
      // Criar timeline a partir dos dados da API
      if (instanceDetail.activities && instanceDetail.activities.length > 0) {
        setTimelineLoading(true);
        try {
          const timelineData = createTimelineFromApiData(
            instanceDetail.instance,
            instanceDetail.activities,
            instanceDetail.variables
          );
          setCompleteTimelineData(timelineData);
          console.log(`Timeline criada com ${timelineData.timeline.length} eventos`);
        } catch (error) {
          console.error('Erro ao criar timeline:', error);
          setSnackbar({
            open: true,
            message: 'Erro ao processar timeline do processo',
            severity: 'error',
          });
        } finally {
          setTimelineLoading(false);
        }
      } else {
        console.warn('Nenhuma atividade encontrada para a instância');
        setTimelineLoading(false);
      }
    }
  }, [instanceDetail, loadBpmn]);

  // Função para alternar visualização detalhada de variável
  const toggleDetailedView = (variableId: string) => {
    const newDetailed = new Set(detailedVariables);
    if (newDetailed.has(variableId)) {
      newDetailed.delete(variableId);
    } else {
      newDetailed.add(variableId);
    }
    setDetailedVariables(newDetailed);
  };

  // Função para expandir/recolher todas as variáveis
  const toggleAllExpanded = () => {
    if (showAllExpanded) {
      setDetailedVariables(new Set());
    } else {
      const allVariableIds = new Set(filteredVariables.map(v => v.id));
      setDetailedVariables(allVariableIds);
    }
    setShowAllExpanded(!showAllExpanded);
  };

  // Manipular clique em evento da timeline
  const handleTimelineEventClick = (event: TimelineEvent) => {
    setSelectedTimelineEvent(event);
    console.log('Evento selecionado:', event);
  };

  // Manipular clique em atividade no diagrama
  const handleActivityClick = (activityId: string) => {
    console.log('Atividade clicada:', activityId);
  };

  // Manipular mudança de aba
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para recriar tarefa de usuário
  const handleRecreateUserTask = async (activityId: string) => {
    if (!currentTenant || !instanceId || !instanceDetail) {
      return;
    }

    try {
      await processesService.recreateUserTask(
        currentTenant.name,
        instanceId,
        {
          process_definition_id: instanceDetail.instance.process_definition_id,
          activity_id: activityId,
          skip_custom_listeners: false,
        }
      );

      setSnackbar({
        open: true,
        message: 'Tarefa recriada com sucesso!',
        severity: 'success',
      });

      // Recarregar os detalhes da instância
      await loadInstanceDetail();
    } catch (err) {
      console.error('❌ Erro ao recriar tarefa:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? (err as unknown as Error).message : 'Erro ao recriar tarefa',
        severity: 'error',
      });
    }
  };


  // Função para obter cor do status
  const getStatusColor = (status: string): 'success' | 'primary' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'ACTIVE':
        return 'primary';
      case 'SUSPENDED':
        return 'warning';
      case 'EXTERNALLY_TERMINATED':
      case 'INTERNALLY_TERMINATED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Função para obter ícone do status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle />;
      case 'ACTIVE':
        return <PlayArrow />;
      case 'SUSPENDED':
        return <PauseCircle />;
      case 'EXTERNALLY_TERMINATED':
      case 'INTERNALLY_TERMINATED':
        return <Error />;
      default:
        return <AccessTime />;
    }
  };

  // Filtrar variáveis baseado na busca
  const filteredVariables = instanceDetail?.variables.filter(variable =>
    variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    variable.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(variable.value).toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Todas as variáveis filtradas serão exibidas na tabela unificada

  // Renderização de erro
  if (error) {
    return (
      <Box 
        sx={{ 
          p: 3,
          marginLeft: "100px",
          minHeight: "100vh",
          backgroundColor: "#f5f5f5"
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadInstanceDetail}
        >
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  // Renderização de loading
  if (loading || !instanceDetail) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          marginLeft: "100px",
          backgroundColor: "#f5f5f5"
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando detalhes da instância...
        </Typography>
      </Box>
    );
  }

  const { instance, variables_count } = instanceDetail;

  return (
    <Box 
      sx={{ 
        p: 3,
        marginLeft: "100px",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5"
      }}
    >
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            variant="outlined"
            size="small"
          >
            Voltar
          </Button>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            Detalhes da Instância
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadInstanceDetail}
            disabled={loading}
            size="small"
          >
            Atualizar
          </Button>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          {instance.process_name} ({instance.process_key})
        </Typography>
      </Box>

      {/* Informações da Instância */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Informações Básicas" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    ID da Instância
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {instance.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    ID da Definição
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {instance.process_definition_id}
                  </Typography>
                </Box>
                {instance.business_key && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Business Key
                    </Typography>
                    <Typography variant="body1">
                      {instance.business_key}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Status e Tempo" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={getStatusIcon(instance.status)}
                    label={instance.status}
                    color={getStatusColor(instance.status)}
                    variant="outlined"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Início
                  </Typography>
                  <Typography variant="body1">
                    {new Date(instance.start_time).toLocaleString('pt-BR')}
                  </Typography>
                </Box>
                {instance.end_time && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Fim
                    </Typography>
                    <Typography variant="body1">
                      {new Date(instance.end_time).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total de Variáveis
                  </Typography>
                  <Typography variant="body1">
                    {variables_count}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Abas de conteúdo */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="Abas de detalhes">
            <Tab 
              icon={<Timeline />} 
              label="Timeline do Processo" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<TableChart />} 
              label={`Variáveis (${filteredVariables.length})`} 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<Error />} 
              label={`Incidentes (${instanceDetail?.incidents_count || 0})`} 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<Assignment />} 
              label={`Tarefas de Usuário (${instanceDetail?.activities?.filter(a => a.activity_type === 'userTask').length || 0})`} 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {/* Conteúdo da aba Timeline */}
        {currentTab === 0 && (
          <Box sx={{ p: 2 }}>
            {bpmnLoading || timelineLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  {bpmnLoading ? 'Carregando diagrama BPMN...' : 'Processando timeline...'}
                </Typography>
              </Box>
            ) : completeTimelineData ? (
              <BpmnTimeline
                timeline={completeTimelineData}
                bpmnXml={bpmnXml}
                onEventClick={handleTimelineEventClick}
                onActivityClick={handleActivityClick}
                selectedEventId={selectedTimelineEvent?.id}
                showOnlyExecutedPath={false}
                variables={instanceDetail?.variables || []}
                incidents={instanceDetail?.incidents || []}
              />
            ) : (
              <Alert severity="info">
                Nenhuma atividade encontrada para esta instância de processo.
              </Alert>
            )}
          </Box>
        )}

        {/* Conteúdo da aba Variáveis */}
        {currentTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                Variáveis do Processo ({filteredVariables.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={toggleAllExpanded}
                >
                  {showAllExpanded ? 'Recolher Todas' : 'Expandir Todas'}
                </Button>
                <TextField
                  size="small"
                  placeholder="Buscar variáveis..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300 }}
                />
              </Box>
            </Box>

            {filteredVariables.length === 0 ? (
              <Alert severity="info">
                Nenhuma variável encontrada com o filtro aplicado.
              </Alert>
            ) : (
              <CompactVariablesTable
                variables={filteredVariables}
                onToggleDetailed={toggleDetailedView}
                detailedVariables={detailedVariables}
              />
            )}
          </Box>
        )}

        {/* Conteúdo da aba Incidentes */}
        {currentTab === 2 && (
          <Box sx={{ p: 3 }}>
            <ProcessIncidents
              incidents={instanceDetail?.incidents || []}
              incidentsCount={instanceDetail?.incidents_count || 0}
              activities={instanceDetail?.activities || []}
            />
          </Box>
        )}

        {/* Conteúdo da aba Tarefas de Usuário */}
        {currentTab === 3 && (
          <Box>
            <UserTasks
              activities={instanceDetail?.activities || []}
              instanceId={instanceId || ''}
              processDefinitionId={instanceDetail?.instance.process_definition_id || ''}
              tenantName={currentTenant?.name || ''}
              onRecreateTask={handleRecreateUserTask}
            />
          </Box>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessInstanceDetail;
